import {
  ContainerItemSchemaSetting,
  SharedItemOverlayDisplayMode,
  GsStructTextAlignment,
  GsStructVerticalAlignment
} from 'libs/shared/src/models';
import {
  ContainerViewTemplateName,
  VIEW_TEMPLATE_SETTINGS
} from 'libs/shared/src/template/app/components/container-gallery/models/view-template-settings';
import { GsFormField, GsFormFieldOption, GsFormIfArg, GsFormSchema, GsFormWithIf } from '../../../components';
import { borderSchemaFields, PICTURE_DISPLAY_MODE_WITH_PHOTOS_SCHEMA } from '../basics';
import { SchemaService } from '../../../services/forms/schemas/schema.service';
import { ACTIVE_SCHEMA_FIELDS } from './../basics/active.schema';
import { BASIC_SCHEMA_FIELDS } from './../basics/basic.schema';
import { generatePaginatedSchemaFields } from './../basics/paginated.schema';
import { generateSwiperSchemaFields } from './../basics/swiper.schema';
import { teaserBadgeSchemaFields } from './../basics/teaser-badge.schema';
import { SPACING_SCHEMA } from './spacing.schema';

/**
 * Get view-template args for a specific setting
 * @param setting The setting to get the args for (JUST 'and' chaining supported)
 * @description Returns the args JUST for 'and' chaining ('or' is not supported with template-options)
 * @returns The view-template args
 */
function getViewTemplateArgs(setting: ContainerItemSchemaSetting): Array<GsFormIfArg> {
  return VIEW_TEMPLATE_SETTINGS.filter((template) => !template.supportedSettings?.includes(setting)).map((template) => ({
    formControlName: 'viewTemplate',
    operator: '!=',
    value: template.name
  }));
}

function applyViewTemplateArgs(
  field: GsFormField<unknown>,
  settingOverride?: ContainerItemSchemaSetting
): GsFormField<unknown> & GsFormWithIf {
  const setting = settingOverride ?? (field.formControlName as ContainerItemSchemaSetting);
  const templateArgs = getViewTemplateArgs(setting);
  if (!templateArgs.length) return field;
  const existingIf = SchemaService.isGsFormWithIf(field) ? field.if : undefined;

  return {
    ...field,
    if: {
      ...(existingIf ?? {}),
      args: [...(existingIf?.args ?? []), ...templateArgs]
    }
  };
}

/**
 * Sets the items per row to 1 if the selected view-template does not support the 'elements' setting.
 * @description The items-per-row fields are hidden for those view-templates, so the schema default (4) would be saved.
 * The backend uses itemsRowDesktop to decide whether the original image is needed, so a hidden 4 leads to blurry images.
 * @param value The form value of the container-gallery
 * @returns The value with corrected items per row
 */
export function normalizeContainerGalleryValue(value: Record<string, unknown>): Record<string, unknown> {
  const viewTemplate = VIEW_TEMPLATE_SETTINGS.find((template) => template.name === value['viewTemplate']);
  if (!viewTemplate || viewTemplate.supportedSettings?.includes('elements')) return value;
  return { ...value, itemsRowDesktop: 1, itemsRowTablet: 1, itemsRowMobile: 1 };
}

const PAGINATED_SCHEMA_FIELDS_WITH_TEMPLATES = generatePaginatedSchemaFields().map((field) => applyViewTemplateArgs(field, 'elements'));
const BORDER_SCHEMA_FIELDS_WITH_TEMPLATES = borderSchemaFields().map((field) => applyViewTemplateArgs(field, 'imageBorder'));
/**
 * Generate slider-specific schema fields from swiper preset with view template conditions
 * Filters out pagination and grid-related fields, keeping only slider controls
 */
const SLIDER_SCHEMA_FIELDS_WITH_TEMPLATES = generateSwiperSchemaFields(false, [], false)
  .filter((field) => {
    // Keep only slider-specific fields, exclude pagination/grid items and sliderSpaceBetween
    const sliderFieldNames = [
      'sliderTeaseItem',
      'sliderTeaseItemSize',
      'sliderAmountGroup',
      'sliderNavigation',
      'sliderAutoplay',
      'sliderDelay',
      'sliderSpeed',
      'sliderLoop'
    ];
    return sliderFieldNames.includes(field.formControlName);
  })
  .map((field) => applyViewTemplateArgs(field, 'sliderOptions'));

export const CONTAINER_GALLERY_COMPONENT_FORM_SCHEMA: GsFormSchema = {
  fields: [
    ...ACTIVE_SCHEMA_FIELDS,
    ...BASIC_SCHEMA_FIELDS,
    ...BORDER_SCHEMA_FIELDS_WITH_TEMPLATES,
    {
      formControlName: 'viewTemplate',
      label: 'components.containerGallery.schema.viewTemplate.label',
      type: 'select',
      options: getViewTemplateSchemaSelectOptions(),
      description: 'components.containerGallery.schema.viewTemplate.description',
      default: VIEW_TEMPLATE_SETTINGS[0]?.name,
      valueType: 'string'
    },
    ...SPACING_SCHEMA,
    {
      formControlName: 'galleryHeight',
      label: 'components.containerGallery.schema.galleryHeight',
      isResponsive: true,
      type: 'number',
      default: { default: 340, lg: 600 },
      if: {
        args: getViewTemplateArgs('galleryHeight')
      }
    },
    {
      formControlName: 'sliderSpaceBetween',
      label: 'schema.items.spaceBetween',
      type: 'number',
      default: 30,
      groupingName: 'schema.items.name',
      if: {
        args: getViewTemplateArgs('boxSpaceBetween')
      }
    },
    ...PAGINATED_SCHEMA_FIELDS_WITH_TEMPLATES,

    {
      formControlName: 'itemPadding',
      label: 'schema.items.itemPadding',
      type: 'padding',
      useTailwindClasses: true,
      isResponsive: true,
      groupingName: 'schema.items.name',
      default: {
        default: {
          top: 2,
          right: 2,
          bottom: 2,
          left: 2
        }
      },
      if: {
        args: [...getViewTemplateArgs('itemPadding')]
      }
    },
    {
      formControlName: 'showPhotos',
      label: 'components.searchGallery.schema.showPhotos',
      type: 'checkbox',
      groupingName: 'schema.items.name',
      default: true,
      if: {
        args: [...getViewTemplateArgs('elements')]
      }
    },
    {
      formControlName: 'forceSquareImage',
      label: 'schema.items.forceSquareImage',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.items.name',
      if: {
        args: [...getViewTemplateArgs('elements'), { formControlName: 'showPhotos', value: true }]
      }
    },
    {
      formControlName: 'itemImageHeight',
      label: 'schema.items.itemImageHeight',
      type: 'number',
      isResponsive: true,
      default: { default: 200 },
      groupingName: 'schema.items.name',
      if: {
        args: [
          ...getViewTemplateArgs('elements'),
          { formControlName: 'showPhotos', value: true },
          { formControlName: 'forceSquareImage', value: false }
        ]
      }
    },
    // Image/Box Settings
    {
      formControlName: 'imageOverlay',
      label: 'components.containerGallery.schema.image.overlayActive',
      type: 'checkbox',
      default: false,
      groupingName: 'components.containerGallery.schema.image.group',
      if: {
        args: [...getViewTemplateArgs('imageOverlay')]
      }
    },
    {
      formControlName: 'imageOverlayTransparency',
      label: 'components.containerGallery.schema.image.overlayTransparency',
      type: 'number',
      default: 20,
      min: 0,
      max: 95,
      groupingName: 'components.containerGallery.schema.image.group',
      if: {
        args: [...getViewTemplateArgs('imageOverlayTransparency'), { formControlName: 'imageOverlay', value: true }]
      }
    },
    {
      formControlName: 'imageOverlayColor',
      label: 'components.containerGallery.schema.image.overlayColor',
      type: 'color',
      default: 'dark-main',
      groupingName: 'components.containerGallery.schema.image.group',
      if: {
        args: [...getViewTemplateArgs('imageOverlayColor'), { formControlName: 'imageOverlay', value: true }]
      }
    },
    // Slider Settings

    ...SLIDER_SCHEMA_FIELDS_WITH_TEMPLATES,
    {
      formControlName: 'sliderSpaceBetween',
      label: 'schema.swiper.spaceBetween',
      type: 'number',
      default: 20,
      groupingName: 'schema.swiper.options',
      if: {
        args: getViewTemplateArgs('sliderSpaceBetween')
      }
    },
    {
      formControlName: 'sliderNavigationType',
      label: 'schema.swiper.navigationType',
      type: 'select',
      valueType: 'number',
      default: 1,
      groupingName: 'schema.swiper.options',
      options: [
        { text: 'schema.swiper.navigationTypes.bullet', value: 0 },
        { text: 'schema.swiper.navigationTypes.scrollbar', value: 1 },
        { text: 'schema.swiper.navigationTypes.none', value: 2 }
      ],
      if: {
        args: getViewTemplateArgs('sliderNavigationType')
      }
    },
    {
      formControlName: 'sliderColumns',
      label: 'schema.swiper.rowsOfItems',
      type: 'number',
      default: 1,
      min: 1,
      groupingName: 'schema.swiper.options',
      if: {
        args: getViewTemplateArgs('sliderColumns')
      }
    },
    // Text Settings
    {
      formControlName: 'itemTextAlignment',
      label: 'schema.alignment.text.name',
      type: 'select',
      valueType: 'number',
      default: GsStructTextAlignment.left,
      groupingName: 'components.containerGallery.schema.text.group',
      options: [
        { text: 'schema.alignment.text.left', value: GsStructTextAlignment.left },
        { text: 'schema.alignment.text.center', value: GsStructTextAlignment.center },
        { text: 'schema.alignment.text.right', value: GsStructTextAlignment.right },
        { text: 'schema.alignment.text.justify', value: GsStructTextAlignment.justify }
      ],
      if: {
        args: getViewTemplateArgs('elements')
      }
    },
    {
      formControlName: 'itemVerticalAlignment',
      label: 'schema.alignment.vertical',
      type: 'select',
      valueType: 'number',
      default: GsStructVerticalAlignment.middle,
      groupingName: 'components.containerGallery.schema.text.group',
      options: [
        { text: 'schema.alignment.text.top', value: GsStructVerticalAlignment.top },
        { text: 'schema.alignment.text.middle', value: GsStructVerticalAlignment.middle },
        { text: 'schema.alignment.text.bottom', value: GsStructVerticalAlignment.bottom }
      ],
      if: {
        args: getViewTemplateArgs('verticalAlignment')
      }
    },
    {
      formControlName: 'showItemSubtitle',
      label: 'components.searchGallery.schema.text.showItemSubtitle.label',
      type: 'checkbox',
      groupingName: 'components.containerGallery.schema.text.group',
      description: 'components.searchGallery.schema.text.showItemSubtitle.description',
      default: true,
      if: {
        args: getViewTemplateArgs('showItemSubtitle')
      }
    },
    {
      formControlName: 'shortenItemTitles',
      label: 'components.searchGallery.schema.text.shortenItemTitles.label',
      type: 'checkbox',
      groupingName: 'components.containerGallery.schema.text.group',
      description: 'components.searchGallery.schema.text.shortenItemTitles.description',
      default: false,
      if: {
        args: getViewTemplateArgs('shortenItemTitles')
      }
    },
    {
      formControlName: 'textColor',
      groupingName: 'components.containerGallery.schema.text.group',
      label: 'components.containerGallery.schema.text.textColor',
      type: 'color',
      default: 'light-main',
      if: {
        args: getViewTemplateArgs('textColor')
      }
    },
    {
      formControlName: 'textShadow',
      groupingName: 'components.containerGallery.schema.text.group',
      label: 'components.containerGallery.schema.text.textShadow',
      type: 'checkbox',
      default: true,
      if: {
        args: getViewTemplateArgs('textShadow')
      }
    },
    {
      formControlName: 'textBackground',
      groupingName: 'components.containerGallery.schema.text.group',
      label: 'components.containerGallery.schema.text.textBackground',
      type: 'checkbox',
      default: false,
      if: {
        args: getViewTemplateArgs('textBackground')
      }
    },
    {
      formControlName: 'textBackgroundColor',
      groupingName: 'components.containerGallery.schema.text.group',
      label: 'components.containerGallery.schema.text.textBackgroundColor',
      type: 'color',
      default: 'primary-main',
      if: {
        args: [
          ...getViewTemplateArgs('textBackgroundColor'),
          {
            formControlName: 'textBackground',
            operator: '==',
            value: true
          }
        ]
      }
    },
    // Hover Settings
    {
      formControlName: 'hoverLinkHint',
      label: 'components.searchGallery.shared.itemHoverLinkHint',
      type: 'label',
      nullable: true,
      readonly: true,
      groupingName: 'schema.hover.name',
      if: {
        args: getViewTemplateArgs('hover')
      }
    },
    {
      formControlName: 'itemZoomOnHover',
      label: 'components.image.schema.zoomOnHover',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.hover.name',
      if: {
        args: getViewTemplateArgs('hover')
      }
    },
    {
      formControlName: 'itemHoverText',
      label: 'components.searchGallery.shared.itemHoverText',
      type: 'text',
      default: 'Mehr erfahren',
      groupingName: 'schema.hover.name',
      if: {
        args: getViewTemplateArgs('hover')
      }
    },
    {
      formControlName: 'itemHoverTextColor',
      label: 'components.searchGallery.shared.itemHoverTextColor',
      type: 'color',
      default: 'primary-contrast',
      groupingName: 'schema.hover.name',
      if: {
        args: [...getViewTemplateArgs('hover'), { formControlName: 'itemHoverText', value: '', operator: '!=' }]
      }
    },
    {
      formControlName: 'itemShowHoverBackground',
      label: 'components.searchGallery.shared.itemShowHoverBackground',
      type: 'checkbox',
      default: true,
      groupingName: 'schema.hover.name',
      if: {
        args: getViewTemplateArgs('hover')
      }
    },
    {
      formControlName: 'itemHoverBackgroundColor',
      label: 'components.searchGallery.shared.itemHoverBackgroundColor',
      type: 'color',
      default: 'primary-main',
      groupingName: 'schema.hover.name',
      if: {
        args: [...getViewTemplateArgs('hover'), { formControlName: 'itemShowHoverBackground', value: true }]
      }
    },
    ...teaserBadgeSchemaFields({
      additionalIfArgs: getViewTemplateArgs('itemTeaser')
    }),
    {
      formControlName: 'newForDays',
      label: 'components.searchGallery.schema.newForDays',
      type: 'number',
      default: 10,
      min: 1,
      groupingName: 'components.searchGallery.schema.itemTeaserOptions',
      if: {
        args: getViewTemplateArgs('itemTeaser')
      }
    },
    {
      formControlName: 'showShoppingCartButton',
      label: 'components.searchGallery.shared.showShoppingCartButton',
      type: 'checkbox',
      default: false,
      if: {
        args: getViewTemplateArgs('quickBuy')
      }
    },
    {
      formControlName: 'showItemButton',
      label: 'components.containerGallery.schema.showItemButton',
      type: 'checkbox',
      default: false,
      groupingName: 'components.containerGallery.schema.itemButton',
      if: {
        args: getViewTemplateArgs('elements')
      }
    },
    {
      formControlName: 'itemButtonTextByArticleGroup',
      label: 'schema.items.itemButtonTextByArticleGroup.label',
      description: 'schema.items.itemButtonTextByArticleGroup.description',
      type: 'checkbox',
      default: false,
      groupingName: 'components.containerGallery.schema.itemButton',
      if: {
        args: [...getViewTemplateArgs('elements'), { formControlName: 'showItemButton', value: true }]
      }
    },
    {
      formControlName: 'itemButtonText',
      label: 'components.containerGallery.schema.itemButtonText',
      type: 'text',
      default: '',
      placeholder: 'components.containerGallery.schema.itemButtonTextPlaceholder',
      groupingName: 'components.containerGallery.schema.itemButton',
      if: {
        args: [...getViewTemplateArgs('elements'), { formControlName: 'showItemButton', value: true }]
      }
    },
    {
      formControlName: 'itemOverlayDisplayMode',
      label: 'components.containerGallery.schema.itemOverlayDisplayMode.label',
      description: 'components.containerGallery.schema.itemOverlayDisplayMode.description',
      type: 'select',
      valueType: 'number',
      default: SharedItemOverlayDisplayMode.onlyWithText,
      if: {
        args: getViewTemplateArgs('elements')
      },
      options: [
        {
          text: 'components.containerGallery.schema.itemOverlayDisplayMode.options.never',
          value: SharedItemOverlayDisplayMode.never
        },
        {
          text: 'components.containerGallery.schema.itemOverlayDisplayMode.options.onlyWithText',
          value: SharedItemOverlayDisplayMode.onlyWithText
        },
        {
          text: 'components.containerGallery.schema.itemOverlayDisplayMode.options.always',
          value: SharedItemOverlayDisplayMode.always
        }
      ]
    },
    ...PICTURE_DISPLAY_MODE_WITH_PHOTOS_SCHEMA
  ],
  normalizeValue: normalizeContainerGalleryValue
};

/**
 * Get select options for view-templates
 * @returns The select options
 */
function getViewTemplateSchemaSelectOptions(): GsFormFieldOption<ContainerViewTemplateName>[] {
  return VIEW_TEMPLATE_SETTINGS.map((template) => ({
    text: template.name.toLocaleUpperCase(),
    value: template.name
  }));
}
